import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { GradeFlowShell } from './GradeFlowShell';
import { GradePipelineSteps } from './GradePipelineSteps';
import { PageWrapper } from '../PageWrapper';

export type GradeSyncProgressState = {
  current: number;
  total: number;
  message: string;
};

export type GradeSyncingViewProps = {
  courseNameHeader?: string;
  isOnline: boolean;
  isDarkMode: boolean;
  setIsDarkMode: Dispatch<SetStateAction<boolean>>;
  syncStatus: 'idle' | 'ok' | 'error';
  onSyncClick: () => void;
  syncProgress: GradeSyncProgressState;
};

export function GradeSyncingView({
  courseNameHeader,
  isOnline,
  isDarkMode,
  setIsDarkMode,
  syncStatus,
  onSyncClick,
  syncProgress,
}: GradeSyncingViewProps): ReactElement {
  const { t } = useTranslation();
  return (
    <PageWrapper
      headerTitle={t('gradeSync.syncing.headerTitle')}
      headerSubtitle={courseNameHeader || undefined}
      isOnline={isOnline}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      syncStatus={syncStatus}
      onSyncClick={onSyncClick}
    >
      <GradeFlowShell
        step={t('gradeSync.syncing.stepLabel')}
        title={t('gradeSync.syncing.title')}
        subtitle={t('gradeSync.syncing.subtitle')}
      >
        <GradePipelineSteps activeIndex={3} className="mb-4" />
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Loader2 className="mb-6 h-12 w-12 animate-spin text-indigo-500" />
          <p className="text-[16px] font-bold uppercase tracking-widest text-slate-500">
            {syncProgress.message}
          </p>
          <div className="mt-6 h-2 w-full max-w-xs overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <div
              className="h-full bg-indigo-500 transition-all duration-300"
              style={{
                width: `${(syncProgress.current / Math.max(1, syncProgress.total)) * 100}%`,
              }}
            />
          </div>
        </div>
      </GradeFlowShell>
    </PageWrapper>
  );
}
